import { createSlice } from '@reduxjs/toolkit';

const whatsNextSlice = createSlice({
    name: 'whatsNext',
    initialState: {
        // Tree of nodes: { id, track, parentId, childIds, x, y, selected }
        nodes: {},
        // Order of node IDs for rendering
        nodeOrder: [],
        // Currently active node (the one we're expanding from)
        activeNodeId: null,
        // Pending recommendations for the active node
        recommendations: [],
        // Loading state
        isLoading: false,
        error: null,
        // The set (ordered list of selected track node IDs forming the path)
        setPath: [],
        // Canvas offset for panning
        panX: 0,
        panY: 0,
        zoom: 1,
        // Set externally (e.g. from a track context menu elsewhere in the app)
        // to seed a new set. WhatsNextTab consumes and clears it on mount.
        // Shape: { query: string, meta: { title, artist, artwork, previewUrl } }
        pendingStart: null,
    },
    reducers: {
        resetFlow: (state) => {
            state.nodes = {};
            state.nodeOrder = [];
            state.activeNodeId = null;
            state.recommendations = [];
            state.isLoading = false;
            state.error = null;
            state.setPath = [];
            state.panX = 0;
            state.panY = 0;
            state.zoom = 1;
        },
        addNode: (state, action) => {
            const node = action.payload;
            state.nodes[node.id] = node;
            if (!state.nodeOrder.includes(node.id)) {
                state.nodeOrder.push(node.id);
            }
        },
        setActiveNode: (state, action) => {
            state.activeNodeId = action.payload;
        },
        setRecommendations: (state, action) => {
            state.recommendations = action.payload;
        },
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        addToSetPath: (state, action) => {
            state.setPath.push(action.payload);
        },
        removeRecommendationNodes: (state, action) => {
            // Remove recommendation nodes that weren't selected
            const keepIds = new Set(action.payload);
            const toRemove = state.nodeOrder.filter(id => {
                const node = state.nodes[id];
                return node && node.isRecommendation && !keepIds.has(id);
            });
            toRemove.forEach(id => {
                delete state.nodes[id];
            });
            state.nodeOrder = state.nodeOrder.filter(id => !toRemove.includes(id));
        },
        updateNodePosition: (state, action) => {
            const { id, x, y } = action.payload;
            if (state.nodes[id]) {
                state.nodes[id].x = x;
                state.nodes[id].y = y;
            }
        },
        setPan: (state, action) => {
            state.panX = action.payload.x;
            state.panY = action.payload.y;
        },
        setZoom: (state, action) => {
            state.zoom = action.payload;
        },
        markNodeSelected: (state, action) => {
            const id = action.payload;
            if (state.nodes[id]) {
                state.nodes[id].isRecommendation = false;
                state.nodes[id].selected = true;
            }
        },
        undoLastTrack: (state) => {
            if (state.setPath.length <= 1) return;
            // Remove the last track from the path
            const removedId = state.setPath.pop();
            // Delete the node
            if (removedId) {
                delete state.nodes[removedId];
                state.nodeOrder = state.nodeOrder.filter(id => id !== removedId);
            }
            // Set active to the new last node
            const newLastId = state.setPath[state.setPath.length - 1];
            state.activeNodeId = newLastId;
            state.error = null;
        },
        breakChainAt: (state, action) => {
            const nodeId = action.payload;
            const idx = state.setPath.indexOf(nodeId);
            if (idx <= 0) return; // Can't remove first node
            // Remove this node and everything after it
            const removedIds = state.setPath.splice(idx);
            removedIds.forEach(id => {
                delete state.nodes[id];
            });
            state.nodeOrder = state.nodeOrder.filter(id => !removedIds.includes(id));
            // Set active to the new last node
            const newLastId = state.setPath[state.setPath.length - 1];
            state.activeNodeId = newLastId;
            state.error = null;
        },
        updateNodeTrack: (state, action) => {
            const { id, track } = action.payload;
            if (state.nodes[id]) {
                state.nodes[id].track = { ...state.nodes[id].track, ...track };
            }
        },
        setPendingStart: (state, action) => {
            state.pendingStart = action.payload;
        },
        clearPendingStart: (state) => {
            state.pendingStart = null;
        },
        loadSavedState: (state, action) => {
            const { nodes, nodeOrder, setPath, activeNodeId, panX, panY, zoom } = action.payload;
            state.nodes = nodes;
            state.nodeOrder = nodeOrder;
            state.setPath = setPath;
            state.activeNodeId = activeNodeId;
            state.panX = panX;
            state.panY = panY;
            state.zoom = zoom;
            state.recommendations = [];
            state.isLoading = false;
            state.error = null;
        },
    },
});

export const {
    resetFlow,
    addNode,
    setActiveNode,
    setRecommendations,
    setLoading,
    setError,
    addToSetPath,
    removeRecommendationNodes,
    updateNodePosition,
    setPan,
    setZoom,
    markNodeSelected,
    undoLastTrack,
    breakChainAt,
    updateNodeTrack,
    setPendingStart,
    clearPendingStart,
    loadSavedState,
} = whatsNextSlice.actions;

export default whatsNextSlice.reducer;
